<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MasifiSettingsAPI from 'dashboard/api/masifiSettings';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import { useStore } from 'vuex';
import MasifiWaGroupsSettings from './MasifiWaGroupsSettings.vue';

// Sección unificada de settings propias de Masifi ("Masifi Inbox"). Cada knob del
// backend (Masifi::Settings::REGISTRY) se renderiza como una card. Hoy: aviso de duplicado.
const { t } = useI18n();
const store = useStore();

const isLoading = ref(true);
const duplicateOwnerAlert = ref(true);
const isolateLeads = ref(true);
const notifyNewConversation = ref(false);
const autoAcceptOnReply = ref(true);
const crmEnabled = ref(true);
const whatsappQrEnabled = ref(true);
// Solo aparece si el owner habilitó feature_whatsapp_qr por cuenta (Masifi::Settings.resolved
// filtra por `requires`): sin entitlement, la card no se muestra (nada que apagar).
const hasWhatsappQr = ref(false);
// Grupos de WhatsApp (F3a): 4 knobs que el backend solo expone con el entitlement QR (requires); la card, además, solo
// con el módulo prendido por el admin.
const GROUP_KEYS = [
  'whatsapp_qr_group_leads',
  'whatsapp_qr_group_auto_assign',
  'whatsapp_qr_group_auto_messages',
  'whatsapp_qr_group_bots',
];
const groupSettings = ref({});
const hasGroupSettings = computed(
  () =>
    hasWhatsappQr.value &&
    whatsappQrEnabled.value &&
    GROUP_KEYS.every(key => key in groupSettings.value)
);
const updateGroupSetting = async (key, value) => {
  const previous = groupSettings.value[key];
  groupSettings.value = { ...groupSettings.value, [key]: value };
  try {
    await MasifiSettingsAPI.update({ [key]: value });
    useAlert(t('MASIFI_SETTINGS.API.SAVE_SUCCESS'));
  } catch (error) {
    groupSettings.value = { ...groupSettings.value, [key]: previous };
    useAlert(t('MASIFI_SETTINGS.API.SAVE_ERROR'));
  }
};

const fetchSettings = async () => {
  isLoading.value = true;
  try {
    const { data } = await MasifiSettingsAPI.get();
    duplicateOwnerAlert.value = Boolean(
      data.settings?.duplicate_lead_owner_alert
    );
    isolateLeads.value = Boolean(data.settings?.isolate_leads_on_reassign);
    notifyNewConversation.value = Boolean(
      data.settings?.notify_on_new_conversation
    );
    autoAcceptOnReply.value = Boolean(data.settings?.auto_accept_on_reply);
    crmEnabled.value = Boolean(data.settings?.crm_enabled);
    hasWhatsappQr.value = 'whatsapp_qr_enabled' in (data.settings || {});
    whatsappQrEnabled.value = Boolean(data.settings?.whatsapp_qr_enabled);
    groupSettings.value = Object.fromEntries(
      GROUP_KEYS.filter(key => key in (data.settings || {})).map(key => [
        key,
        Boolean(data.settings[key]),
      ])
    );
  } catch (error) {
    useAlert(t('MASIFI_SETTINGS.API.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Persiste un knob; revierte el toggle local si la API falla. Reusado por cada card.
const persist = async (key, value, model) => {
  try {
    await MasifiSettingsAPI.update({ [key]: value });
    useAlert(t('MASIFI_SETTINGS.API.SAVE_SUCCESS'));
  } catch (error) {
    model.value = !value; // revertir si falló
    useAlert(t('MASIFI_SETTINGS.API.SAVE_ERROR'));
  }
};

const updateDuplicateOwner = value =>
  persist('duplicate_lead_owner_alert', value, duplicateOwnerAlert);
const updateIsolateLeads = value =>
  persist('isolate_leads_on_reassign', value, isolateLeads);
const updateNotifyNewConversation = value =>
  persist('notify_on_new_conversation', value, notifyNewConversation);
const updateAutoAcceptOnReply = value =>
  persist('auto_accept_on_reply', value, autoAcceptOnReply);
const updateCrmEnabled = async value => {
  await persist('crm_enabled', value, crmEnabled);
  // Refresca el record de la cuenta en el store → el gate del sidebar (Policy feature-flag,
  // que lee account.features) re-evalúa crm_active? al instante, sin recargar la página.
  store.dispatch('accounts/get');
};
const updateWhatsappQrEnabled = async value => {
  await persist('whatsapp_qr_enabled', value, whatsappQrEnabled);
  // Mismo motivo que crmEnabled: feature_whatsapp_qr en enabled_features depende del gate
  // efectivo (whatsapp_qr_active?), y el panel/asistente de alta lo leen del store.
  store.dispatch('accounts/get');
};

onMounted(fetchSettings);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="t('MASIFI_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('MASIFI_SETTINGS.TITLE')"
        :description="t('MASIFI_SETTINGS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-4 pb-8">
        <SettingsToggleSection
          v-model="crmEnabled"
          :header="t('MASIFI_SETTINGS.CRM_MODULE.HEADER')"
          :description="t('MASIFI_SETTINGS.CRM_MODULE.DESCRIPTION')"
          @update:model-value="updateCrmEnabled"
        />
        <SettingsToggleSection
          v-model="duplicateOwnerAlert"
          :header="t('MASIFI_SETTINGS.DUPLICATE_OWNER.HEADER')"
          :description="t('MASIFI_SETTINGS.DUPLICATE_OWNER.DESCRIPTION')"
          @update:model-value="updateDuplicateOwner"
        />
        <SettingsToggleSection
          v-model="isolateLeads"
          :header="t('MASIFI_SETTINGS.ISOLATE_LEADS.HEADER')"
          :description="t('MASIFI_SETTINGS.ISOLATE_LEADS.DESCRIPTION')"
          @update:model-value="updateIsolateLeads"
        />
        <SettingsToggleSection
          v-model="notifyNewConversation"
          :header="t('MASIFI_SETTINGS.NEW_CONVERSATION.HEADER')"
          :description="t('MASIFI_SETTINGS.NEW_CONVERSATION.DESCRIPTION')"
          @update:model-value="updateNotifyNewConversation"
        />
        <SettingsToggleSection
          v-model="autoAcceptOnReply"
          :header="t('MASIFI_SETTINGS.AUTO_ACCEPT.HEADER')"
          :description="t('MASIFI_SETTINGS.AUTO_ACCEPT.DESCRIPTION')"
          @update:model-value="updateAutoAcceptOnReply"
        />
        <SettingsToggleSection
          v-if="hasWhatsappQr"
          v-model="whatsappQrEnabled"
          :header="t('MASIFI_SETTINGS.WHATSAPP_QR.HEADER')"
          :description="t('MASIFI_SETTINGS.WHATSAPP_QR.DESCRIPTION')"
          @update:model-value="updateWhatsappQrEnabled"
        />
        <MasifiWaGroupsSettings
          v-if="hasGroupSettings"
          :settings="groupSettings"
          @update="updateGroupSetting"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
